import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import FooterSection from '../components/FooterSection';

const { FiTarget, FiEye, FiMessageSquare, FiMap, FiChevronDown, FiArrowRight, FiFileText, FiCheck } = FiIcons;

const PDF_LINK = "https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/documents/YXBwbGljYXRpb24vcGRm/ea9cc330a70307b149e1c4f1c2db8e1b.pdf";
const PAYMENT_LINK = "https://www.paypal.com/ncp/payment/9TB4QQL3GQH9C";

const faqs = [
  {
    question: "How does the brand report work?",
    answer: "WE COMBINE DATA-DRIVEN ALGORITHMS AND BRAND ARCHETYPE PRINCIPLES TO GENERATE YOUR BRAND'S POSITIONING. OUR TOOL CREATES A POSITIONING STATEMENT AND VISUAL IDENTITY UNIQUE TO YOU BRAND BASED ON THE ANSWERS YOU PROVIDE IN THE QUESTIONNAIRE."
  },
  {
    question: "How is this different from other AI platforms?",
    answer: "OUR METHODOLOGY USES PROVEN BRANDING FRAMEWORKS FOR RELIABLE OUTPUT AND WAS BUILT BY FORMER BRANDING EXPERTS. THE USE OF ADVANCED LLMs PAIRED WITH SEVERAL APIs ALLOWS US TO PROVIDE A MORE EXTENSIVE ANALYSIS."
  },
  {
    question: "What is the step-by-step process for generating a brand strategy?",
    answer: "YOU PROVIDE US WITH THE FOLLOWING INFORMATION ABOUT YOUR BRAND:\n\n• BASIC INFORMATION.\n• EXPLORE AND DEFINE YOUR BRAND'S INSPIRATION.\n• IDENTIFY AND DESCRIBE YOUR IDEAL CUSTOMER PROFILE.\n• INPUT KEY COMPETITORS.\n• DEFINE BRAND VALUES.\n• SELECT BRAND ARCHETYPES.\n\nONCE WE HAVE THAT INFORMATION WE GENERATE YOUR BRAND REPORT.\nIN 5-7 BUSINESS DAYS YOU RECEIVE YOUR REPORT."
  },
  {
    question: "What is included in the final brand report?",
    answer: "IN YOUR REPORT YOU RECEIVE:\n\n• BRAND POSITIONING\n• BRAND IDENTITY\n• BRAND ARCHETYPE\n• BRAND NARRATIVE\n• VISUAL IDENTITY\n• YOUR BRAND NOW\n• SWOT ANALYSIS\n• COMPETITOR ANALYSIS\n• LANDING PAGE ANALYSIS\n• SEO INSIGHTS\n• LIGHTHOUSE REPORT SUMMARY\n• MARKET STRATEGY\n• IDEAL CUSTOMER PROFILE\n• CONTENT STRATEGY\n• MARKETING CAMPAIGN IDEAS"
  },
  {
    question: "Why choose sans titre?",
    answer: "OUR FOUNDER HAS OVER A DECADE OF EXPERIENCE IN THE MEDIA AND MARKETING INDUSTRIES.\nDURING THAT TIME WE'VE WORKED WITH:\n• HSN (HOME SHOPPING NETWORK)\n• NIKE\n• HOME DEPOT\n• CHEWY\n• DIOR\n• NEW BALANCE\n• MLB\n• VARIOUS AMAZON SELLERS"
  }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/10 py-6">
      <button 
        className="w-full flex justify-between items-center text-left focus:outline-none group"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h4 className="text-xl md:text-2xl font-serif text-white tracking-wide">{question}</h4>
        <div className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <SafeIcon icon={FiChevronDown} className="w-5 h-5 text-white/50 group-hover:text-white transition-colors" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-4 text-white/60 text-sm md:text-base leading-relaxed whitespace-pre-line font-light">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function BrandReport() {
  return (
    <div className="min-h-screen bg-black text-white font-body selection:bg-white/30">
      
      {/* Navbar */}
      <nav className="absolute top-0 left-0 w-full z-50 px-6 py-6 border-b border-transparent">
        <div className="liquid-glass rounded-full max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="w-6 h-6 mr-3 flex items-center justify-center shrink-0">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/cd6c64635923cfb3391950ed67895587.png" 
                alt="Logo" 
                className="w-full h-full object-contain"
              />
            </Link>
            <span className="text-white font-semibold text-lg uppercase tracking-wider">SANS TITRE</span>
            <div className="hidden md:flex items-center gap-8 ml-8 text-white/80 text-sm font-medium">
              <Link to="/brand-report" className="text-white transition-colors border-b border-white pb-0.5">Brand Report</Link>
              <Link to="/portfolio" className="hover:text-white transition-colors">Portfolio</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/contact" className="text-white text-sm font-medium hidden md:block hover:text-white/80 transition-colors">Connect</Link>
            <Link to="/outpost" className="liquid-glass rounded-full px-6 py-2 text-white text-sm font-medium hover:bg-white/5 transition-colors block">
              THE OUTPOST
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          muted
          playsInline
          autoPlay
          loop
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260324_151826_c7218672-6e92-402c-9e45-f1e0f454bdc4.mp4"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black z-0 pointer-events-none" />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl lg:text-8xl font-serif text-white tracking-tight mb-8"
          >
            Clarity for the modern brand
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}
            className="text-lg md:text-xl text-white/70 font-light leading-relaxed max-w-2xl mx-auto mb-12"
          >
            A comprehensive, data-driven audit of your brand's identity, positioning, and market competition. Stop guessing, start scaling with intention.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6"
          >
            <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer" className="bg-white text-black px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-white/90 transition-colors uppercase w-full sm:w-auto">
              Request Brand Audit
            </a>
            <a href={PDF_LINK} target="_blank" rel="noopener noreferrer" className="liquid-glass text-white px-8 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-white/10 transition-colors uppercase flex items-center gap-2 w-full sm:w-auto justify-center">
              <SafeIcon icon={FiFileText} className="w-4 h-4" />
              View Sample Report
            </a>
          </motion.div>
        </div>
      </section>

      {/* Anatomy Section */}
      <section className="py-32 px-6 bg-black relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">Anatomy of the report</h2>
            <p className="text-white/60 text-lg font-light max-w-2xl mx-auto leading-relaxed">
              Our comprehensive brand analysis leaves no stone unturned. We dissect your brand to rebuild it stronger, cleaner, and more profitable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16">
            {[
              { icon: FiTarget, title: "Brand positioning", desc: "Identify your exact place in the market and uncover uncontested space among competitors." },
              { icon: FiEye, title: "Visual identity audit", desc: "A ruthless breakdown of your current design system, logo usage, typography, and color psychology." },
              { icon: FiMessageSquare, title: "Messaging strategy", desc: "Refining your tone of voice, core messaging pillars, and taglines to resonate with your ideal audience." },
              { icon: FiMap, title: "Actionable roadmap", desc: "Not just theory. You receive a step-by-step strategic plan to implement the recommended changes." }
            ].map((item, i) => (
              <div key={i} className="border border-white/10 rounded-2xl p-8 hover:bg-white/[0.02] transition-colors group">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-6 border border-white/10 group-hover:border-white/30 transition-colors">
                  <SafeIcon icon={item.icon} className="w-5 h-5 text-white/80" />
                </div>
                <h3 className="text-2xl font-serif text-white mb-3">{item.title}</h3>
                <p className="text-white/50 font-light leading-relaxed text-sm md:text-base">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer" className="inline-block border border-white/20 text-white px-10 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-white hover:text-black transition-all uppercase">
              Order Brand Analysis
            </a>
          </div>
        </div>
      </section>

      {/* Side-by-side Strategy Section */}
      <section className="py-24 px-6 bg-white text-black relative z-10 transition-colors">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            
            {/* Block 1 */}
            <div className="border border-black p-8 md:p-12 flex flex-col justify-between min-h-[400px] relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none text-black">
                 <SafeIcon icon={FiFileText} className="w-64 h-64" />
              </div>
              <div className="relative z-10">
                <span className="text-xs uppercase tracking-[0.3em] font-medium text-black/60">Confidential</span>
              </div>
              <div className="relative z-10 my-16">
                <h3 className="text-5xl md:text-6xl font-serif leading-[1.1] text-black">The brand<br />architecture<br />report.</h3>
              </div>
              <div className="relative z-10 border-t border-black pt-6 mt-auto">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-black/60 text-sm uppercase tracking-wider">Prepared by</span>
                  <span className="text-black text-sm font-medium">Sans Titre</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-black/60 text-sm uppercase tracking-wider">Format</span>
                  <span className="text-black text-sm font-medium">50+ Page Digital Deck</span>
                </div>
              </div>
            </div>

            {/* Block 2 */}
            <div className="border border-black p-8 md:p-12 flex flex-col justify-between min-h-[400px]">
              <div>
                <h3 className="text-2xl font-serif mb-6 uppercase tracking-widest text-black">Strategy over aesthetic</h3>
                <p className="text-black/80 font-light leading-relaxed mb-8">
                  Beautiful design is meaningless without a solid foundation. This report is for founders and marketers who are serious about treating their brand as a business asset, not just an art project.
                </p>
                <ul className="space-y-4">
                  {[
                    "Deep-dive competitor analysis framework",
                    "Customer archetype & persona definition",
                    "Core value proposition refinement",
                    "Tone of voice guidelines",
                    "Visual direction & moodboarding"
                  ].map((bullet, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="mt-1">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-black" />
                      </div>
                      <span className="text-black/90 font-light">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>

          <div className="text-center">
            <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer" className="inline-block bg-black text-white px-10 py-4 rounded-full text-sm font-medium tracking-wide hover:bg-black/90 transition-all uppercase">
              Order Intelligence Report
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 px-6 bg-black relative z-10 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-2">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-6 bg-black relative z-10 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.05)_0%,_transparent_70%)] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-5xl md:text-7xl font-serif text-white mb-8 leading-[1.1]">
            Ready to define your narrative?
          </h2>
          <p className="text-xl text-white/60 font-light mb-12 max-w-2xl mx-auto">
            Secure your comprehensive brand audit report today and start building a brand that commands authority.
          </p>
          
          <div className="flex flex-col items-center justify-center gap-4">
            <a href={PAYMENT_LINK} target="_blank" rel="noopener noreferrer" className="bg-white text-black px-12 py-5 rounded-full text-sm font-bold tracking-widest hover:bg-white/90 transition-all uppercase shadow-[0_0_40px_rgba(255,255,255,0.2)] hover:shadow-[0_0_60px_rgba(255,255,255,0.3)]">
              Order Your Report - $499
            </a>
            <span className="text-white/40 text-xs tracking-widest uppercase mt-4">
              *Turn around time: 5-7 business days
            </span>
          </div>

          <div className="mt-24 pt-16 border-t border-white/10">
             <p className="text-white/50 text-sm uppercase tracking-widest mb-6">Know what to expect</p>
             <a href={PDF_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 liquid-glass px-8 py-4 rounded-full text-white hover:bg-white/10 transition-colors group">
                <SafeIcon icon={FiFileText} className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="font-medium tracking-wide uppercase text-sm">View Example Brand Report</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
             </a>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}